/*
 * BP-84 EXPERIMENT — evaluate api-football widgets; remove or keep behind flag
 * per product decision.
 *
 * Deliberately self-contained: this single file + its SCSS module are the whole
 * feature (plus a two-line route registration in App.tsx), so reverting is a
 * `git rm` of two files and one route. Not linked from any navigation.
 *
 * Text is hardcoded English (no i18n) — dev-only evaluation surface.
 *
 * How the third-party script works (verified by reading
 * https://widgets.api-sports.io/2.0.3/widgets.js): it registers a single
 * `DOMContentLoaded` handler that looks up `#wg-api-football-games` /
 * `#wg-api-football-standings` by id and reads their `data-*` attributes. In an
 * SPA the document is already parsed by the time we mount, so after loading the
 * script we dispatch a synthetic `DOMContentLoaded` to trigger the same code
 * path. Changing league/season remounts the hosts via `key` and re-dispatches.
 */
import React, { useEffect, useRef, useState } from 'react';
import styles from './TestWidgetsPage.module.scss';

const WIDGET_SCRIPT_SRC = 'https://widgets.api-sports.io/2.0.3/widgets.js';
const WIDGET_HOST = 'v3.football.api-sports.io';

const DEFAULT_LEAGUE = '39';
const DEFAULT_SEASON = '2025';

const TestWidgetsPage: React.FC = () => {
  // Never hardcode a key — evaluation requires an explicitly provided env var.
  const apiKey = import.meta.env?.VITE_API_FOOTBALL_WIDGET_KEY as string | undefined;

  const [league, setLeague] = useState(DEFAULT_LEAGUE);
  const [season, setSeason] = useState(DEFAULT_SEASON);

  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const instanceKey = `${league}-${season}`;

  // widgets.js registers via window.addEventListener('DOMContentLoaded'), so
  // the synthetic event must bubble from document up to window (a non-bubbling
  // Event dispatched on document never reaches window listeners).
  const boot = () => document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true }));

  // Load the script ONCE per page mount and never remove it on re-renders: the
  // module pulls a ~20-file import graph from the CDN, and re-appending it on
  // every league/season change (or StrictMode double-mount) refetches the whole
  // graph and gets the client 429-rate-limited by widgets.api-sports.io.
  useEffect(() => {
    if (!apiKey) return undefined;
    if (scriptRef.current) {
      boot();
      return undefined;
    }

    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT_SRC;
    script.type = 'module';
    script.async = true;
    script.addEventListener('load', boot);
    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      script.removeEventListener('load', boot);
      script.remove();
      scriptRef.current = null;
    };
  }, [apiKey]);

  // League/season change: hosts were remounted empty via `key` — re-run the
  // widget init against the already-loaded module.
  useEffect(() => {
    if (!apiKey || !scriptRef.current) return;
    boot();
  }, [apiKey, instanceKey]);

  if (!apiKey) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>API-Football widgets — evaluation</h1>
        <div className={styles.instructions}>
          <p>
            No widget API key configured. Set <code>VITE_API_FOOTBALL_WIDGET_KEY</code> and
            reload:
          </p>
          <ul>
            <li>
              Docksal: add <code>VITE_API_FOOTBALL_WIDGET_KEY=&lt;key&gt;</code> to{' '}
              <code>.docksal/docksal-local.env</code>, then <code>fin restart</code>.
            </li>
            <li>
              Plain Vite dev server: add the same line to{' '}
              <code>packages/telegram-mini/.env.local</code> and restart the dev server.
            </li>
          </ul>
          <p>
            Use a throwaway / restricted key. Never commit a key and never reuse the production
            server key here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>API-Football widgets — evaluation</h1>

      <div className={styles.warning} role="alert">
        Widgets expose the API key to the browser and consume quota per viewer — evaluation only.
      </div>

      <div className={styles.controls}>
        <label className={styles.control}>
          League id
          <input
            type="number"
            className={`field ${styles.input}`}
            value={league}
            onChange={(e) => setLeague(e.target.value)}
            data-testid="widget-league-input"
          />
        </label>
        <label className={styles.control}>
          Season
          <input
            type="number"
            className={`field ${styles.input}`}
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            data-testid="widget-season-input"
          />
        </label>
      </div>

      <section className={styles.widgetBlock}>
        <span className={styles.widgetLabel}>Games</span>
        <div className={styles.widgetHost}>
          <div
            key={`games-${instanceKey}`}
            id="wg-api-football-games"
            data-host={WIDGET_HOST}
            data-key={apiKey}
            data-league={league}
            data-season={season}
            data-theme="dark"
            data-refresh="0"
            data-show-toolbar="true"
            data-show-errors="true"
            data-show-logos="true"
            data-modal-game="true"
            data-modal-standings="true"
            data-modal-show-logos="true"
          />
        </div>
      </section>

      {/* Widget bug in football/2.0.3/library/standings.js: with
          data-show-errors="true" the render ALWAYS early-returns after the
          error loop — even with zero errors — leaving the table empty. It must
          stay "false" on the standings host for the table to render. */}
      <section className={styles.widgetBlock}>
        <span className={styles.widgetLabel}>Standings</span>
        <div className={styles.widgetHost}>
          <div
            key={`standings-${instanceKey}`}
            id="wg-api-football-standings"
            data-host={WIDGET_HOST}
            data-key={apiKey}
            data-league={league}
            data-season={season}
            data-theme="dark"
            data-show-errors="false"
            data-show-logos="true"
          />
        </div>
      </section>
    </div>
  );
};

export default TestWidgetsPage;
